import { useEffect, useSyncExternalStore } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { Resource } from '@medplum/fhirtypes';
import { getPractitionerDisplayTimeZone } from '@unify/fhir-model/resources/practitioner';
import {
  getPreferredDisplayTimeZoneGeneration,
  getPreferredDisplayTimeZoneSnapshot,
  resolveDisplayTimeZone,
  setPreferredDisplayTimeZone,
  subscribePreferredDisplayTimeZone,
} from '@/lib/unifyDateFormat';

/**
 * Current display IANA zone (Practitioner preference, else browser, else UTC).
 * Subscribes to the module store so Settings saves re-render consumers immediately.
 */
export function useDisplayTimeZone(): string {
  useSyncExternalStore(
    subscribePreferredDisplayTimeZone,
    getPreferredDisplayTimeZoneSnapshot,
    getPreferredDisplayTimeZoneSnapshot,
  );
  return resolveDisplayTimeZone().timeZone;
}

/**
 * Hydrates the signed-in Practitioner's saved display timezone into the module store.
 *
 * Call this once from the app shell. `useMedplumProfile()` returns the cached login
 * profile, which does NOT reflect extension writes made with `updateResource` until
 * the next login, so the Practitioner is re-read with `{ cache: 'no-cache' }` (same
 * reason as `useGeminiAiOptOut`).
 *
 * In-flight reads are ignored when `setPreferredDisplayTimeZone` has run since they
 * started (generation token), so a Settings save cannot be overwritten by a stale fetch.
 */
export function usePreferredDisplayTimeZone(profile: Resource | undefined): string {
  const medplum = useMedplum();
  const practitionerId = profile?.resourceType === 'Practitioner' ? profile.id : undefined;
  const loading = medplum.isLoading();

  useEffect(() => {
    if (loading) return;
    if (!practitionerId) {
      setPreferredDisplayTimeZone(null);
      return;
    }
    const generation = getPreferredDisplayTimeZoneGeneration();
    let cancelled = false;
    void medplum
      .readResource('Practitioner', practitionerId, { cache: 'no-cache' })
      .then((practitioner) => {
        if (cancelled) return;
        if (getPreferredDisplayTimeZoneGeneration() !== generation) return;
        setPreferredDisplayTimeZone(getPractitionerDisplayTimeZone(practitioner) ?? null);
      })
      .catch(() => {
        // Non-blocking — keep the browser-zone fallback.
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, practitionerId, loading]);

  return useDisplayTimeZone();
}
